import { BulkOperators } from '@/lib/enums';

function applyPropertyChanges(currentProps = [], propChanges) {
  const { create = [], update = [], remove = [] } = propChanges;

  const mutation = {
    create: [],
    update: [],
    remove: [],
  };

  const currentMap = new Map(currentProps.map((p) => [p.key, p.value]));
  let hasChanges = false;

  create.forEach((prop) => {
    const key = prop.property_uuid || prop.key;
    const value = prop.value || prop.option_uuid || '';
    if (key && !currentMap.has(key)) {
      mutation.create.push({ key, value });
      hasChanges = true;
    }
  });

  update.forEach((prop) => {
    const key = prop.property_uuid || prop.key;
    const value = prop.value || prop.option_uuid || '';
    if (key && currentMap.has(key) && currentMap.get(key) !== value) {
      mutation.update.push({ key, value });
      hasChanges = true;
    }
  });

  remove.forEach((key) => {
    if (currentMap.has(key)) {
      mutation.remove.push(key);
      hasChanges = true;
    }
  });

  if (!hasChanges) {
    return null;
  }

  Object.keys(mutation).forEach((action) => {
    if (mutation[action].length === 0) {
      delete mutation[action];
    }
  });

  return mutation;
}

/**
 * @param {Object} snapshot
 * @param {Object} bulk
 * @param {String[]} bulk.uuids
 * @param {String} [bulk.property]
 * @param {Number} [bulk.value]
 * @param {String} [bulk.operator]
 * @param {Object} [bulk.properties]
 * @returns {Object}
 */
export default function bulk(snapshot, bulk) {
  const now = Date.now();

  const mutation = {
    created_at: now,
    entities: {
      groups: {
        update: [],
      },
      roles: {
        update: [],
      },
      activities: {
        update: [],
      },
    },
  };

  if (!snapshot) {
    return mutation;
  }

  const { uuids, property, value, operator, properties } = bulk;

  const updatedGroups = new Set([]);
  const updatedRoles = new Set([]);

  const convertValue = (operator, originalValue, newValue) => {
    switch (operator) {
      case BulkOperators.INCREASE:
        return originalValue + newValue;
      case BulkOperators.INCREASE_PERCENTAGE:
        return originalValue + originalValue * (newValue / 100);
      case BulkOperators.DECREASE:
        return originalValue - newValue;
      case BulkOperators.DECREASE_PERCENTAGE:
        return originalValue - originalValue * (newValue / 100);
      case BulkOperators.SET:
      default:
        return newValue;
    }
  };

  uuids.forEach((uuid) => {
    if (!Object.hasOwn(snapshot.entities.activities, uuid)) {
      return;
    }

    const entity = snapshot.entities.activities[uuid];

    const activityUpdate = {
      uuid,
      updated_at: now,
    };

    if (property && value !== undefined) {
      const originalValue = entity[property];
      let newValue = convertValue(operator, originalValue, value);

      if (property === 'hours') {
        newValue = Math.max(0, Math.min(newValue, 999));
        newValue = Math.round(newValue * 100) / 100;
      }

      activityUpdate[property] = newValue;
    }

    if (properties && typeof properties === 'object') {
      const propertiesInput = applyPropertyChanges(
        entity.properties,
        properties
      );
      if (propertiesInput) {
        activityUpdate.properties = propertiesInput;
      }
    }

    mutation.entities.activities.update.push(activityUpdate);

    const role = snapshot.entities.roles[entity.owner_uuid];
    if (role) {
      updatedRoles.add(entity.owner_uuid);

      const group = snapshot.entities.groups[role.group_uuid];
      if (group) {
        updatedGroups.add(group.uuid);
      }
    }
  });

  updatedRoles.forEach((roleId) => {
    mutation.entities.roles.update.push({
      uuid: roleId,
      updated_at: now,
    });
  });

  updatedGroups.forEach((groupId) => {
    mutation.entities.groups.update.push({
      uuid: groupId,
      updated_at: now,
    });
  });

  return mutation;
}
